import { useState, useEffect } from 'react';
import { Modal, Button, Group, Stack } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { colors } from '../../theme/colors';

interface DatePickerModalProps {
  /** モーダルの開閉状態 */
  opened: boolean;
  /** 登録ボタンのラベル */
  submitLabel: string;
  /** 初期選択日付 */
  initialDate?: Date;
  /** カレンダーの初期表示月（日付が未選択時に表示する月） */
  defaultDate?: Date;
  /** 選択可能な最小日付（指定すると以前のセルが disabled） */
  minDate?: Date;
  /** 選択可能な最大日付（指定すると以降のセルが disabled） */
  maxDate?: Date;
  /** 日付選択後の登録コールバック */
  onSubmit: (date: Date) => void;
  /** キャンセルコールバック */
  onCancel: () => void;
  /**
   * 削除コールバック（任意）。指定すると赤の削除ボタンを表示する。
   * 収穫・輸送の日付ダイアログのように「既存イベントの削除」を同じダイアログから行う用途で使う。
   */
  onDelete?: () => void;
  /** 削除ボタンのラベル（`onDelete` 指定時のみ有効。既定は「削除」） */
  deleteLabel?: string;
}

/**
 * Date を YYYY-MM-DD 形式の文字列に変換
 */
function dateToString(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * YYYY-MM-DD 形式の文字列を Date に変換
 */
function stringToDate(str: string | null): Date | null {
  if (!str) return null;
  const [year, month, day] = str.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * 日付ピッカーモーダル
 *
 * 月間カレンダー形式で日付を選択できるモーダルコンポーネント
 *
 */
export function DatePickerModal({
  opened,
  submitLabel,
  initialDate,
  defaultDate,
  minDate,
  maxDate,
  onSubmit,
  onCancel,
  onDelete,
  deleteLabel = '削除',
}: DatePickerModalProps) {
  // Mantine DatePicker は YYYY-MM-DD 形式の文字列を使用
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(
    dateToString(initialDate ?? null)
  );

  // カレンダーの表示月（controlled）
  const [displayDate, setDisplayDate] = useState<Date>(initialDate ?? defaultDate ?? new Date());

  // モーダルが開かれたときに初期日付と表示月をセット
  useEffect(() => {
    if (opened) {
      setSelectedDateStr(dateToString(initialDate ?? null));
      setDisplayDate(initialDate ?? defaultDate ?? new Date());
    }
  }, [opened, initialDate, defaultDate]);

  const handleSubmit = () => {
    const date = stringToDate(selectedDateStr);
    if (date) {
      onSubmit(date);
    }
  };

  const handleCancel = () => {
    setSelectedDateStr(dateToString(initialDate ?? null));
    onCancel();
  };

  return (
    <Modal opened={opened} onClose={handleCancel} withCloseButton={false} centered size="auto">
      <Stack gap="md">
        <DatePicker
          value={selectedDateStr}
          onChange={setSelectedDateStr}
          date={displayDate}
          onDateChange={(dateStr) => {
            const d = stringToDate(dateStr);
            if (d) setDisplayDate(d);
          }}
          minDate={dateToString(minDate ?? null) ?? undefined}
          maxDate={dateToString(maxDate ?? null) ?? undefined}
          size="md"
        />
        <Group justify="flex-end" gap="sm">
          <Button
            variant="outline"
            styles={{ root: { borderColor: colors.dark.main, color: colors.dark.main } }}
            onClick={handleCancel}
          >
            キャンセル
          </Button>
          <Button color="dark" onClick={handleSubmit} disabled={!selectedDateStr}>
            {submitLabel}
          </Button>
          {onDelete && (
            <Button color="red" onClick={onDelete} data-testid="date-picker-delete">
              {deleteLabel}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
